import type { Notification } from '@parlay-picker/shared/notification';
import {
  formatOddsDisplay,
  type NotificationDisplayData,
} from '@parlay-picker/shared/notificationDisplay';
import { getTeamAbbr, getTeamLogo } from '@parlay-picker/shared/teams';
import { usePlayerHeadshotUrl } from '@views/picks/utils';
import type { ReactElement } from 'react';
import { useState } from 'react';
import { COLOR_SCHEME_CLASSES } from './colorScheme';
import { FallbackBody } from './FallbackBody';

/** Leg body: player headshot with team badge + name + team matchup + prop line + result badge */
export function LegBody({
  displayData,
  notification,
}: {
  displayData: NotificationDisplayData;
  notification: Notification;
}): ReactElement {
  const [imgError, setImgError] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const [oppLogoError, setOppLogoError] = useState(false);

  const { leg } = displayData;
  const headshotUrl = usePlayerHeadshotUrl(leg?.playerName, leg?.leagueKey ?? null);
  if (!leg) return <FallbackBody notification={notification} />;

  const colors = COLOR_SCHEME_CLASSES[displayData.colorScheme];

  // Build matchup string: "LAL vs BOS"
  const { leagueKey } = leg;
  const teamAbbr = leg.team ? getTeamAbbr(leg.team, leagueKey) : '';
  const oppAbbr = leg.opponentTeam ? getTeamAbbr(leg.opponentTeam, leagueKey) : '';
  const matchup = teamAbbr && oppAbbr ? `${teamAbbr} vs ${oppAbbr}` : teamAbbr || '';

  // Use formatted label if available, fall back to raw stat type
  const statDisplay = leg.statLabel || leg.statType;

  // Game-level props (spread, total, moneyline): show team logos instead of player headshot
  if (leg.isGameLevel) {
    const mainLogoUrl = getTeamLogo(leg.team, leagueKey);
    const badgeLogoUrl = getTeamLogo(leg.opponentTeam, leagueKey);

    // Build prop line: show full prop + stat label
    const propParts: string[] = [];
    if (leg.prop) propParts.push(leg.prop);
    if (statDisplay && statDisplay !== leg.prop) propParts.push(statDisplay);
    const propLine =
      propParts.length > 0
        ? propParts.join(' · ')
        : [leg.overUnder, leg.line, statDisplay].filter(Boolean).join(' ');

    return (
      <div className="flex items-center gap-2 mt-1.5">
        {/* Team logo with opponent badge */}
        <div className="relative shrink-0">
          {mainLogoUrl && !imgError ? (
            <img
              src={mainLogoUrl}
              alt={teamAbbr}
              className="w-11 h-11 rounded-full object-cover bg-theme-surface-hover ring-2 ring-theme-border/50"
              onError={() => setImgError(true)}
            />
          ) : (
            <div
              className={`w-11 h-11 rounded-full flex items-center justify-center text-xs font-bold ${colors.iconBg} ${colors.iconText}`}
            >
              {teamAbbr || '?'}
            </div>
          )}
          {badgeLogoUrl && !oppLogoError && (
            <img
              src={badgeLogoUrl}
              alt={oppAbbr}
              className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-theme-surface border border-theme-border"
              onError={() => setOppLogoError(true)}
            />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <p className="text-sm font-bold text-theme-text truncate">
              {matchup || leg.playerName}
            </p>
            {leg.odds && (
              <span
                className={`font-mono text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0 ${colors.badgeBg} ${colors.badgeText}`}
              >
                {formatOddsDisplay(leg.odds)}
              </span>
            )}
          </div>
          {propLine && (
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="text-xs text-theme-text-secondary font-mono truncate">
                {propLine}
              </span>
              {leg.actualValue && leg.actualValue !== '?' && (
                <span className="text-xs text-theme-text-tertiary shrink-0">
                  · <span className="font-mono font-medium">{leg.actualValue}</span>
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Player-level props: show player headshot with team badge
  const teamLogoUrl = getTeamLogo(leg.team, leagueKey);

  return (
    <div className="flex items-center gap-2 mt-1.5">
      {/* Player headshot with team badge */}
      <div className="relative shrink-0">
        {headshotUrl && !imgError ? (
          <img
            src={headshotUrl}
            alt={leg.playerName}
            className="w-11 h-11 rounded-full object-cover bg-theme-surface-hover ring-2 ring-theme-border/50"
            onError={() => setImgError(true)}
          />
        ) : (
          <div
            className={`w-11 h-11 rounded-full flex items-center justify-center text-xs font-bold ${colors.iconBg} ${colors.iconText}`}
          >
            {leg.playerName
              .split(' ')
              .map((w) => w[0])
              .join('')
              .slice(0, 2)
              .toUpperCase()}
          </div>
        )}
        {/* Team logo badge */}
        {teamLogoUrl && !logoError && (
          <img
            src={teamLogoUrl}
            alt={teamAbbr}
            className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-theme-surface border border-theme-border"
            onError={() => setLogoError(true)}
          />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <p className="text-sm font-bold text-theme-text truncate">{leg.playerName}</p>
          {matchup && (
            <span className="text-[10px] font-semibold text-theme-text-tertiary bg-theme-surface-hover px-1.5 py-0.5 rounded shrink-0">
              {matchup}
            </span>
          )}
          {leg.odds && (
            <span
              className={`font-mono text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0 ${colors.badgeBg} ${colors.badgeText}`}
            >
              {formatOddsDisplay(leg.odds)}
            </span>
          )}
        </div>
        {(leg.overUnder || statDisplay) && (
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className="text-xs text-theme-text-secondary font-mono">
              {[leg.overUnder, leg.line, statDisplay].filter(Boolean).join(' ')}
            </span>
            {leg.actualValue && leg.actualValue !== '?' && (
              <span className="text-xs text-theme-text-tertiary shrink-0">
                · <span className="font-mono font-medium">{leg.actualValue}</span>
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
